import { formatIndiaDate, formatIndiaTime } from './indiaTime';

function pickFirst(item, keys) {
  if (!item) return '';
  for (const key of keys) {
    const value = item[key];
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
}

function extractPatientContact(item) {
  const patient =
    item.patient ||
    item.Patient ||
    item.patientDetails ||
    item.PatientDetails ||
    item.patientInfo ||
    item.PatientInfo ||
    {};

  const patientName =
    pickFirst(item, ['patientName', 'PatientName']) ||
    pickFirst(patient, ['fullName', 'FullName', 'patientName', 'PatientName', 'name', 'Name']);

  const patientMobile =
    pickFirst(item, [
      'patientMobile',
      'PatientMobile',
      'mobileNumber',
      'MobileNumber',
      'phoneNumber',
      'PhoneNumber',
    ]) ||
    pickFirst(patient, [
      'mobileNumber',
      'MobileNumber',
      'patientMobile',
      'PatientMobile',
      'phoneNumber',
      'PhoneNumber',
    ]);

  const patientEmail =
    pickFirst(item, [
      'patientEmail',
      'PatientEmail',
      'emailID',
      'EmailID',
      'emailId',
      'EmailId',
      'email',
      'Email',
    ]) ||
    pickFirst(patient, [
      'emailID',
      'EmailID',
      'emailId',
      'EmailId',
      'email',
      'Email',
      'patientEmail',
      'PatientEmail',
    ]);

  const landmark =
    pickFirst(item, ['landmark', 'Landmark']) ||
    pickFirst(patient, ['landmark', 'Landmark']);

  let patientAddress =
    pickFirst(item, ['patientAddress', 'PatientAddress']) ||
    pickFirst(patient, ['patientAddress', 'PatientAddress', 'address', 'Address']);

  if (!patientAddress) {
    const addressLine =
      pickFirst(item, ['addressLine', 'AddressLine']) ||
      pickFirst(patient, ['addressLine', 'AddressLine', 'address', 'Address']);
    const districtName =
      pickFirst(item, ['districtName', 'DistrictName']) ||
      pickFirst(patient, ['districtName', 'DistrictName']);
    const pinCode =
      pickFirst(item, ['pINCode', 'pinCode', 'PinCode', 'PINCode']) ||
      pickFirst(patient, ['pinCode', 'PinCode', 'pINCode', 'PINCode']);

    patientAddress = [addressLine, landmark, districtName, pinCode].filter(Boolean).join(', ');
  }

  return { patientName, patientMobile, patientEmail, patientAddress, landmark };
}

export function mergeDoctorVisitContact(primary, fallback) {
  if (!fallback) return primary;
  return {
    ...primary,
    patientName: primary.patientName || fallback.patientName,
    patientMobile: primary.patientMobile || fallback.patientMobile,
    patientEmail: primary.patientEmail || fallback.patientEmail,
    patientAddress: primary.patientAddress || fallback.patientAddress,
    landmark: primary.landmark || fallback.landmark,
  };
}

export function mapDoctorVisitFromApi(item) {
  const serviceName = item.serviceTypeName || '—';
  const isPhone =
    item.visitMode === 'Phone' ||
    /telephone|tel\./i.test(serviceName);

  const statusKey = (item.status || '').trim().toLowerCase();
  const paymentLabel = item.paymentLabel || (statusKey === 'paid' ? 'Paid' : 'Unpaid');
  const contact = extractPatientContact(item);
  const patient =
    item.patient ||
    item.Patient ||
    item.patientDetails ||
    item.PatientDetails ||
    item.patientInfo ||
    item.PatientInfo ||
    null;

  return {
    id: item.idVisitRequest,
    bookingNumber: item.bookingNumber || `VR-${item.idVisitRequest}`,
    serviceTypeId: item.serviceTypeID,
    serviceType: serviceName,
    visitMode: isPhone ? 'Phone' : 'Home',
    preferredDate: formatIndiaDate(item.preferredDateTime),
    preferredTime: formatIndiaTime(item.preferredDateTime),
    preferredDateTime: item.preferredDateTime,
    amount: Number(item.agreedRate) > 0 ? `₹${Number(item.agreedRate).toLocaleString('en-IN')}` : '—',
    rate: item.agreedRate,
    status: statusKey,
    statusLabel: getVisitStatusLabel(statusKey, paymentLabel),
    description: item.description || '',
    districtName: item.districtName || '—',
    districtId: item.districtID,
    pinCode: item.pINCode || item.pinCode || '',
    locationLabel: item.locationLabel || item.districtName || '—',
    publishedAt: item.publishedAt,
    assignedAt: item.assignedAt,
    completedAt: item.completedAt,
    consultationRemarks: item.consultationRemarks || '',
    patientId: item.patientID ?? item.patientId ?? item.PatientID ?? patient?.idPatient ?? patient?.IdPatient,
    patientName: contact.patientName,
    patientMobile: contact.patientMobile,
    patientAddress: contact.patientAddress,
    patientEmail: contact.patientEmail,
    landmark: contact.landmark,
    showPatientContact: item.showPatientContact === true || item.ShowPatientContact === true,
    canAccept: item.canAccept === true,
    canComplete: item.canComplete === true,
    paymentLabel,
  };
}

export function getVisitStatusLabel(statusKey, paymentLabel) {
  if (statusKey === 'published') return 'Open';
  if (statusKey === 'assigned') return 'Accepted';
  if (statusKey === 'completed') return 'Completed';
  if (statusKey === 'paid') return 'Paid & Closed';
  if (paymentLabel === 'Paid') return 'Paid & Closed';
  return statusKey ? statusKey.charAt(0).toUpperCase() + statusKey.slice(1) : '—';
}

export function getVisitStatusBadgeClass(statusKey) {
  if (statusKey === 'published') return 'badge bg-info';
  if (statusKey === 'assigned') return 'badge bg-warning text-dark';
  if (statusKey === 'completed') return 'badge bg-success';
  if (statusKey === 'paid') return 'badge bg-secondary';
  return 'badge bg-secondary';
}

export function formatPostedAgo(isoDate) {
  if (!isoDate) return '';
  const then = new Date(isoDate).getTime();
  const now = Date.now();
  const diffMs = now - then;
  if (Number.isNaN(diffMs) || diffMs < 0) return '';

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 1) return 'Posted just now';
  if (hours < 24) return `Posted ${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.floor(hours / 24);
  return `Posted ${days} day${days === 1 ? '' : 's'} ago`;
}
